import { OrganizationMetrics } from '@prisma/client'
import { Avatar, Divider, Flex, Space, Typography } from 'antd'
import React, { Fragment } from 'react'
import { AgentKey } from '~/agents/common/common/types'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { OrganizationMemberClientShared } from '~/features/organizationMember/clientShared'
import { useLogic } from '../hooks/useLogic'

type Props = {
  member: OrganizationMemberFromContext
  organizationMetrics: OrganizationMetrics
}

export const MetricsAgent: React.FC<Props> = ({
  member,
  organizationMetrics,
}) => {
  const appKey = member.appSession?.app?.key as AgentKey

  const logic = useLogic({ organizationMetrics })
  const employeeTaskMetrics = logic.getLastMonthMetricsForAgent(appKey)

  const role = OrganizationMemberClientShared.Helper.getRole(member)
  const name = OrganizationMemberClientShared.Helper.getName(member)
  const pictureUrl = OrganizationMemberClientShared.Helper.getPictureUrl(member)

  const timeSaved = employeeTaskMetrics.reduce(
    (acc, task) => acc + task.timeSaved,
    0,
  )

  return (
    <Flex vertical gap={16} className="w-full">
      <Flex gap={8}>
        <Flex>
          <Avatar src={pictureUrl} size={60} shape="square" />
        </Flex>

        <Flex vertical flex={1}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {role}
          </Typography.Title>
          <Typography.Text
            type="secondary"
            style={{ margin: 0, fontWeight: 400 }}
          >
            {name}
          </Typography.Text>
        </Flex>
        <Typography.Text
          type="success"
          style={{ fontSize: 18, fontWeight: 500 }}
        >
          {logic.getCountWithUnitMinified(timeSaved)}
        </Typography.Text>
      </Flex>
      <Space />
      <Flex vertical gap={8} justify="center" className="w-full">
        {employeeTaskMetrics?.map((task, index) => (
          <Fragment key={task.type}>
            {index !== 0 && <Divider dashed className="m-0" />}
            <Flex className="w-full">
              <Flex gap={8} flex={1}>
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                  {task.count}{' '}
                  {task.count === 1
                    ? task.config.labels.singular
                    : task.config.labels.plural}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                  &middot;{' '}
                  {logic.getCountWithUnitMinified(task.config.timeEstimate)}/
                  {task.config.labelUnit}
                </Typography.Text>
              </Flex>
              <Typography.Text type="success" style={{ fontSize: 14 }}>
                {logic.getCountWithUnitMinified(task.timeSaved)}
              </Typography.Text>
            </Flex>
          </Fragment>
        ))}
      </Flex>
    </Flex>
  )
}
